import { ChangeDetectionStrategy, Component, HostListener, inject } from '@angular/core';
import { TooltipModule } from 'primeng/tooltip';
import { AssistantService } from '@/cloud/services/assistant.service';
import { TranslationService } from '@/layout/service/translation.service';

/**
 * Top-bar button for the assistant. Only shown where the assistant can be used (this tenant enabled it, or
 * the user has service desk access); a dot flags a bad pulse figure or a proposal awaiting confirmation.
 * ⌘K / Ctrl+K toggles the drawer from anywhere.
 */
@Component({
    selector: 'app-assistant-toggle',
    standalone: true,
    imports: [TooltipModule],
    changeDetection: ChangeDetectionStrategy.OnPush,
    template: `
        @if (assistant.available() && !assistant.fullPage()) {
            <button
                type="button"
                class="layout-topbar-action assistant-toggle"
                [class.active]="assistant.open()"
                (click)="assistant.toggle()"
                [pTooltip]="t()('assistant.shortcut')"
                tooltipPosition="bottom"
                [attr.aria-label]="t()('assistant.button')"
                [attr.aria-pressed]="assistant.open()"
            >
                <i class="pi pi-sparkles" aria-hidden="true"></i>
                @if (assistant.attention() && !assistant.open()) {
                    <span class="attention-dot" aria-hidden="true"></span>
                }
            </button>
        }
    `,
    styles: [
        `
            /* Only the button joins the top-bar row, so the gaps between actions stay even. */
            :host {
                display: inline-flex;
            }
            .assistant-toggle {
                position: relative;
            }
            .assistant-toggle.active {
                color: var(--p-primary-color);
            }
            .assistant-toggle i {
                transition: transform 0.2s;
            }
            .assistant-toggle:hover i {
                transform: rotate(-12deg) scale(1.08);
            }
            .attention-dot {
                position: absolute;
                top: 0.45rem;
                right: 0.45rem;
                width: 0.5rem;
                height: 0.5rem;
                border-radius: 50%;
                background: var(--app-status-warning, #eda100);
                box-shadow: 0 0 0 2px var(--surface-card);
                animation: attention 2s ease-in-out infinite;
            }
            @keyframes attention {
                50% {
                    transform: scale(1.3);
                }
            }
            @media (prefers-reduced-motion: reduce) {
                .attention-dot {
                    animation: none;
                }
            }
        `
    ]
})
export class AssistantToggleComponent {
    readonly assistant = inject(AssistantService);
    readonly t = inject(TranslationService).t;

    @HostListener('document:keydown', ['$event'])
    onKey(event: KeyboardEvent): void {
        if ((event.metaKey || event.ctrlKey) && !event.altKey && !event.shiftKey && event.key.toLowerCase() === 'k' && this.assistant.available() && !this.assistant.fullPage()) {
            event.preventDefault();
            this.assistant.toggle();
        }
    }
}
